/**
 * English sentence splitter (ENGINE-SPEC §2.6, rules R1 to R12, applied in order, per block).
 *
 * A candidate boundary is a cluster of adjacent terminal tokens ('.', '!', '?', ELLIPSIS), then optional
 * closers (" ” ’ ' ) ] } » ›), then whitespace, then the next token. Headings, list items and table cells
 * are one structural sentence each. The end of a block is always a boundary (R9).
 *
 * Reading of the rules where the spec is loose (each one chooses the conservative side):
 *  - R1 also looks through opening quotes and brackets to the first real character ("Wait. “what?”").
 *  - R1 "digit continuing a list": a 1 to 2 digit integer followed by '.' that starts the sentence, follows
 *    ':' ';' ',' '(' or is the next number of a list already begun ("1. Mix 2. Bake") does not end a sentence.
 *  - R2 abbreviations: `entries` always suppress a split, except etc / a.m / p.m, which end the sentence when a
 *    capitalised word follows that does not continue the phrase (not a day, month or time zone: "5 p.m. Monday");
 *    the spec's "known sentence opener" list is widened to any capitalised word, because names and nouns
 *    ("at 6 p.m. Dinner was late") open sentences too. `numericOnly` entries (No, Fig, Dec, Sun …) suppress a
 *    split only before a number or another abbreviation ("Mon. Dec. 5").
 *  - R3 initials: any single uppercase letter + '.' (also "I."), per the spec.
 *  - R4: '.' after a time (6:30) before a number on the same line does not split; a '.' inside a URL/e-mail never sits before
 *    whitespace, so it is never a candidate (R10 exempts URLs and e-mails from the flag).
 *  - R6b (addition): after `!` or `?` and a closing quote that ends a sentence which OPENED with a quote, a
 *    capitalised name or pronoun followed by a speech verb is a dialogue tag ("Stop!" Mary said.): no split.
 *    The spec's R6 only names lowercase tags. `He asked, "Why?" She answered.` still splits.
 *  - R8: a line break after terminal punctuation is ordinary whitespace, so R1 to R6 still apply.
 *  - R10: flagged only when an uppercase letter follows with no space ("end.Next") and the '.' is not part
 *    of an initial, abbreviation, URL or e-mail.
 *  - R11: an em/en dash (or "--") followed directly by a closing double quote, whitespace and an uppercase
 *    word ends the sentence after the quote.
 *  - A sentence never consists of punctuation only: such a tail is merged into the previous sentence (also in the
 *    middle of a block).
 *
 * C4 additions (engine 3.3.0, ENGINE-SPEC §2.6 "C4 additions", docs/reports/c4-split.md section 6): spaced ellipsis
 * `. . .`; `_` and a line-initial `:` do not hide a boundary; initials before abbreviations, with `I.`, `20 K.`,
 * `Exhibit G.`, `nmol/L.` ending a sentence; more abbreviations (abbrev.json en-v3.1), unit, all-caps acronym and `et al.`
 * cases; an inline `1. ... 2. ...` list; and the lowercase-writer mode. That mode is document-level evidence: buildDoc
 * calls `lowerWriterMode` once over all blocks and passes the result in `BlockTokens.lowerWriter`, so this function keeps
 * no state and is a pure function of (block, flag, data).
 */
import {
	SENT_NOSPACE,
	SENT_RUNON,
	SENT_STRUCTURAL,
	TOK_DASH,
	TOK_ELLIPSIS,
	TOK_NUM,
	TOK_PUNCT,
	TOK_WORD,
} from '../../document/types.ts';
import type { BlockTokens, SentenceSink } from '../types.ts';

export const RUNON_WORDS = 120;

// ---- C4 additions (docs/reports/c4-split.md section 6, ENGINE-SPEC §2.6) -------------------------------------------
/** Single-letter abbreviations in abbrev.json (b. c. d. s. v.): they count only when not glued to the previous character. */
const GLUE_GUARDED: ReadonlySet<string> = new Set(['b', 'c', 'd', 's', 'v']);
/** All-caps acronyms that collide with a lowercase abbreviation (MS, EST, CA, CO, AL, HR): a capitalised word after them starts a sentence. */
const AMBIG_ACRONYM: ReadonlySet<string> = new Set(['ms', 'est', 'ca', 'co', 'al', 'hr']);
/** Unit abbreviations: after a number, a capitalised non-numeric word starts a sentence ("0.5 cm. Seated"). */
const UNITS: ReadonlySet<string> = new Set([
	'cm',
	'mm',
	'km',
	'kg',
	'lb',
	'lbs',
	'oz',
	'ml',
	'ft',
	'yd',
	'gal',
	'mi',
	'hr',
	'hrs',
	'qt',
	'tsp',
	'tbsp',
]);
/** A single capital letter after one of these is a label ("Exhibit G.", "Plan B."), not an initial. */
const LABEL_WORDS: ReadonlySet<string> = new Set([
	'exhibit',
	'appendix',
	'plan',
	'option',
	'type',
	'class',
	'grade',
	'vitamin',
	'schedule',
	'annex',
	'table',
	'figure',
	'section',
	'part',
	'group',
	'hepatitis',
	'factor',
	'protein',
	'route',
	'level',
	'stage',
	'phase',
	'tier',
	'track',
	'team',
	'unit',
	'zone',
	'plot',
	'lot',
	'block',
	'item',
	'case',
]);
/** Particles that make a preceding lowercase word part of a name ("Madame de C.", "Ludwig van B."). */
const NAME_PARTICLES: ReadonlySet<string> = new Set([
	'de',
	'van',
	'von',
	'der',
	'den',
	'da',
	'di',
	'du',
	'la',
	'le',
	'del',
	'della',
	'of',
	'bin',
	'ibn',
	'al',
	'el',
]);
/** After "et al." these capitalised words start a new sentence. */
const SENTENCE_OPENERS: ReadonlySet<string> = new Set([
	'this',
	'these',
	'those',
	'the',
	'in',
	'it',
	'its',
	'however',
	'they',
	'we',
	'a',
	'an',
	'there',
	'he',
	'she',
	'our',
	'their',
	'such',
	'for',
	'as',
	'on',
	'at',
	'with',
	'but',
	'however',
	'although',
	'while',
	'when',
	'after',
	'before',
	'both',
	'all',
	'some',
	'many',
	'most',
	'one',
	'two',
	'first',
	'second',
	'then',
	'thus',
	'therefore',
	'also',
	'further',
	'moreover',
]);
/** A document is in "lowercase-writer mode" when at least this share of its sentence-final sites is followed by a lowercase word. */
const LOWER_MODE_SHARE = 0.5;
const LOWER_MODE_MIN_SITES = 4;
/** In that mode a terminal followed by a lowercase word splits only after a clause of at least this many words. */
const LOWER_MODE_MIN_WORDS = 4;

/** After a.m. / p.m. / etc., these capitalised words continue the phrase instead of starting a sentence. */
const CONTINUES_TIME = new Set([
	'monday',
	'tuesday',
	'wednesday',
	'thursday',
	'friday',
	'saturday',
	'sunday',
	'january',
	'february',
	'march',
	'april',
	'may',
	'june',
	'july',
	'august',
	'september',
	'october',
	'november',
	'december',
	'eastern',
	'pacific',
	'central',
	'mountain',
	'gmt',
	'utc',
	'est',
	'edt',
	'pst',
	'pdt',
	'cst',
	'cdt',
	'mst',
	'mdt',
	'bst',
	'cet',
]);

const SPEECH_VERBS = new Set([
	'said',
	'asked',
	'replied',
	'answered',
	'whispered',
	'shouted',
	'cried',
	'called',
	'yelled',
	'exclaimed',
	'murmured',
	'muttered',
	'sighed',
	'added',
	'continued',
	'demanded',
	'snapped',
	'laughed',
	'remarked',
	'insisted',
	'suggested',
	'explained',
	'announced',
	'declared',
	'offered',
	'breathed',
	'screamed',
	'sobbed',
	'hissed',
	'growled',
	'barked',
	'ordered',
	'warned',
	'urged',
	'wondered',
	'responded',
	'retorted',
	'interrupted',
	'stammered',
	'gasped',
	'groaned',
	'mumbled',
	'observed',
	'noted',
	'pleaded',
	'protested',
	'snarled',
	'roared',
	'teased',
	'says',
	'asks',
	'replies',
	'answers',
	'whispers',
	'shouts',
	'cries',
	'calls',
	'adds',
	'continues',
	'thought',
	'begged',
	'agreed',
	'cautioned',
	'chuckled',
	'commanded',
	'conceded',
	'confessed',
	'echoed',
	'inquired',
	'joked',
	'mused',
	'panted',
	'quipped',
	'recalled',
	'repeated',
	'scoffed',
	'stated',
	'swore',
	'wailed',
	'yelped',
]);

const URL_TLD_HINT = /(?:^|[^\p{L}])www\.|@/u;

function isSpaceCode(c: number): boolean {
	return c <= 32 || c === 0xa0;
}

function isCloserChar(ch: number): boolean {
	switch (ch) {
		case 34: // "
		case 39: // '
		case 41: // )
		case 93: // ]
		case 125: // }
		case 0x201d: // ”
		case 0x2019: // ’
		case 0xbb: // »
		case 0x203a: // ›
			return true;
		default:
			return false;
	}
}

function isOpenerChar(ch: number): boolean {
	switch (ch) {
		case 34:
		case 39:
		case 40: // (
		case 91: // [
		case 123: // {
		case 0x201c: // “
		case 0x2018: // ‘
		case 0xab: // «
		case 0x2039: // ‹
		case 0x201e: // „
			return true;
		default:
			return false;
	}
}

/** 1 = uppercase letter, -1 = lowercase letter, 0 = neither (caseless script, digit, symbol). */
function caseOf(text: string, idx: number): number {
	const c = text.charCodeAt(idx);
	if (c < 128) {
		if (c >= 65 && c <= 90) return 1;
		if (c >= 97 && c <= 122) return -1;
		return 0;
	}
	const s = text.slice(idx, idx + (c >= 0xd800 && c <= 0xdbff ? 2 : 1));
	const up = s.toUpperCase();
	const lo = s.toLowerCase();
	if (up === lo) return 0;
	if (s === lo) return -1;
	if (s === up) return 1;
	return 1;
}

export interface SplitData {
	/** Lowercase, no trailing dot. Suppress a split unconditionally. */
	strong: ReadonlySet<string>;
	/** Suppress a split only before a number. */
	numeric: ReadonlySet<string>;
}

/** Abbreviation key of the '.' token at t: dotted chain ("u.s", "e.g") first, then the bare word. */
function abbrevKeyOf(b: BlockTokens, data: SplitData, t: number): string | null {
	const { text, offset, tokens, tokStart } = b;
	const cls = tokens.cls;
	const st = tokens.start;
	const ln = tokens.len;
	const ids = tokens.id;
	const adjacent = (a: number, c: number): boolean =>
		(st[c] as number) === (st[a] as number) + (ln[a] as number);
	const chAt = (x: number): number => text.charCodeAt((st[x] as number) - offset);
	if (t <= tokStart || cls[t - 1] !== TOK_WORD || !adjacent(t - 1, t)) return null;
	let parts = tokens.fold(ids[t - 1] as number);
	let key: string | null = null;
	if (data.strong.has(parts) || data.numeric.has(parts)) key = parts;
	let j = t - 1;
	let guard = 0;
	while (
		guard < 4 &&
		j - 2 >= tokStart &&
		cls[j - 1] === TOK_PUNCT &&
		chAt(j - 1) === 46 &&
		adjacent(j - 1, j) &&
		cls[j - 2] === TOK_WORD &&
		adjacent(j - 2, j - 1) &&
		(ln[j - 2] as number) <= 3
	) {
		parts = `${tokens.fold(ids[j - 2] as number)}.${parts}`;
		if (data.strong.has(parts) || data.numeric.has(parts)) key = parts;
		j -= 2;
		guard++;
	}
	if (key !== null && key.length === 1 && GLUE_GUARDED.has(key)) {
		// a letter glued to a number or word ("HbA1c.", "the 80s.", "4's.") is not a standalone abbreviation
		const before = (st[t - 1] as number) - offset - 1;
		const bc = before >= 0 ? text.charCodeAt(before) : 32;
		if (
			bc === 39 ||
			bc === 0x2019 ||
			(bc >= 48 && bc <= 57) ||
			(bc >= 65 && bc <= 90) ||
			(bc >= 97 && bc <= 122)
		)
			return null;
	}
	return key;
}

/**
 * Lowercase-writer evidence of one block: appends one entry per sentence-final site, 0 when the next word starts
 * with a capital and 1 when it starts with a lowercase letter. A site is a single '.' after a word of two or more
 * letters, followed by whitespace, that is not an abbreviation. Structural blocks have none. Pure in (block, data).
 */
export function lowerFollowSites(b: BlockTokens, data: SplitData, out: number[]): void {
	const { text, offset, tokens, tokStart, tokEnd } = b;
	if (b.structural || tokEnd <= tokStart) return;
	const cls = tokens.cls;
	const st = tokens.start;
	const ln = tokens.len;
	const adjacent = (a: number, c: number): boolean =>
		(st[c] as number) === (st[a] as number) + (ln[a] as number);
	for (let t = tokStart + 1; t + 1 < tokEnd; t++) {
		if (cls[t] !== TOK_PUNCT || text.charCodeAt((st[t] as number) - offset) !== 46) continue;
		if (cls[t - 1] !== TOK_WORD || !adjacent(t - 1, t) || (ln[t - 1] as number) < 2) continue;
		if (adjacent(t, t + 1)) continue; // cluster, closer or run-together
		if (abbrevKeyOf(b, data, t) !== null) continue;
		let q = t + 1;
		while (
			q < tokEnd &&
			cls[q] === TOK_PUNCT &&
			isOpenerChar(text.charCodeAt((st[q] as number) - offset))
		)
			q++;
		if (q >= tokEnd || cls[q] !== TOK_WORD) continue;
		const cs = caseOf(text, (st[q] as number) - offset);
		if (cs === 1) out.push(0);
		else if (cs === -1) out.push(1);
	}
}

/**
 * Lowercase-writer mode of one document (a pure function of its blocks, computed once by buildDoc and handed to the
 * splitter as `BlockTokens.lowerWriter` on every block). The document is in the mode when at least 4 of its
 * sentence-final sites are followed by a word that starts in lowercase and at least half of all its sites are. The
 * splitter then ends a sentence at '.', '!' or '?' after a clause of at least 4 words even before a lowercase word
 * ("i went home. then i slept."). A document-level decision keeps the splitting of a paragraph independent of its
 * neighbours, so a window of the document splits exactly as the whole does (the measurement bench relies on that).
 */
export function lowerWriterMode(blocks: readonly BlockTokens[], data: SplitData): boolean {
	const sites: number[] = [];
	let all = 0;
	let lo = 0;
	for (const b of blocks) {
		sites.length = 0;
		lowerFollowSites(b, data, sites);
		all += sites.length;
		for (const f of sites) lo += f;
	}
	return all >= LOWER_MODE_MIN_SITES && lo / all >= LOWER_MODE_SHARE;
}

export function splitEnglish(b: BlockTokens, out: SentenceSink, data: SplitData): void {
	const { text, offset, tokens, tokStart, tokEnd } = b;
	if (tokEnd <= tokStart) return;
	if (b.structural) {
		out.push(tokStart, tokEnd, SENT_STRUCTURAL);
		return;
	}
	const cls = tokens.cls;
	const st = tokens.start;
	const ln = tokens.len;
	const ids = tokens.id;

	const chAt = (t: number): number => text.charCodeAt((st[t] as number) - offset);
	const adjacent = (a: number, c: number): boolean =>
		(st[c] as number) === (st[a] as number) + (ln[a] as number);
	const isTerminal = (t: number): boolean => {
		const c = cls[t];
		if (c === TOK_ELLIPSIS) return true;
		if (c !== TOK_PUNCT) return false;
		const ch = chAt(t);
		return ch === 46 || ch === 33 || ch === 63;
	};
	const isCloserTok = (t: number): boolean => cls[t] === TOK_PUNCT && isCloserChar(chAt(t));
	const isOpenerTok = (t: number): boolean => cls[t] === TOK_PUNCT && isOpenerChar(chAt(t));

	const abbrevKey = (t: number): string | null => abbrevKeyOf(b, data, t);
	const isStrong = (w: string): boolean => data.strong.has(w);
	const abbreviated = (w: string): boolean => data.strong.has(w) || data.numeric.has(w);
	const rawOf = (t: number): string =>
		text.slice((st[t] as number) - offset, (st[t] as number) - offset + (ln[t] as number));
	const isDotTok = (t: number): boolean => t < tokEnd && cls[t] === TOK_PUNCT && chAt(t) === 46;
	/** Number of dots in a spaced ellipsis ". . ." starting at the '.' token t (single spaces between), else 1. */
	const spacedDots = (t: number): number => {
		let n = 1;
		let a = t;
		while (
			a + 1 < tokEnd &&
			isDotTok(a + 1) &&
			(st[a + 1] as number) === (st[a] as number) + (ln[a] as number) + 1 &&
			text.charCodeAt((st[a] as number) - offset + 1) === 32
		) {
			n++;
			a++;
		}
		return n;
	};
	const isEmphasisMark = (t: number): boolean => cls[t] === TOK_PUNCT && chAt(t) === 95;

	let hasPrev = false;
	let pS = 0;
	let pE = 0;
	let pF = 0;
	const emit = (s: number, e: number, f: number, content: boolean): void => {
		if (!content && hasPrev) {
			pE = e;
			pF |= f;
			return;
		}
		if (hasPrev) out.push(pS, pE, pF);
		pS = s;
		pE = e;
		pF = f;
		hasPrev = true;
	};

	// Lowercase-writer mode: decided once per document by lowerWriterMode (no state here).
	const lowerMode = b.lowerWriter === true;

	let sStart = tokStart;
	let content = false;
	let wc = 0;
	let flags = 0;
	let listNext = 0;
	let i = tokStart;

	while (i < tokEnd) {
		const c = cls[i] as number;
		if (c === TOK_WORD) {
			content = true;
			wc++;
			i++;
			continue;
		}
		if (c === TOK_NUM) {
			content = true;
			i++;
			continue;
		}
		let dash = false;
		if (c === TOK_PUNCT) {
			const ch = chAt(i);
			if (ch !== 46 && ch !== 33 && ch !== 63) {
				if (ch === 0x2014 || ch === 0x2013) dash = true;
				else {
					i++;
					continue;
				}
			}
		} else if (c === TOK_DASH) {
			dash = true;
		} else if (c !== TOK_ELLIPSIS) {
			i++;
			continue;
		}

		if (dash) {
			// R11: dash, closing double quote, whitespace, uppercase.
			if (i + 1 < tokEnd && adjacent(i, i + 1) && cls[i + 1] === TOK_PUNCT) {
				const q = chAt(i + 1);
				if (q === 34 || q === 0x201d || q === 0x2019 || q === 0xbb) {
					let k = i + 1;
					while (k + 1 < tokEnd && adjacent(k, k + 1) && isCloserTok(k + 1)) k++;
					if (k + 1 < tokEnd && (st[k + 1] as number) > (st[k] as number) + (ln[k] as number)) {
						let p = k + 1;
						while (p < tokEnd && isOpenerTok(p)) p++;
						if (
							p < tokEnd &&
							cls[p] === TOK_WORD &&
							caseOf(text, (st[p] as number) - offset) === 1 &&
							content
						) {
							if (wc > RUNON_WORDS) flags |= SENT_RUNON;
							emit(sStart, k + 1, flags, true);
							sStart = k + 1;
							content = false;
							wc = 0;
							flags = 0;
						}
					}
					i = k + 1;
					continue;
				}
			}
			i++;
			continue;
		}

		// Terminal cluster i..j, closers j+1..k.
		let j = i;
		while (j + 1 < tokEnd && adjacent(j, j + 1) && isTerminal(j + 1)) j++;
		let spaced = false;
		if (i === j && isDotTok(i)) {
			const sd = spacedDots(i);
			if (sd >= 3) {
				j = i + sd - 1;
				spaced = true;
			}
		}
		let dots = 0;
		for (let t = i; t <= j; t++)
			if (cls[t] === TOK_ELLIPSIS) dots += 3;
			else if (isDotTok(t)) dots++;
		const ell = spaced || dots >= 3;
		let k = j;
		while (k + 1 < tokEnd && adjacent(k, k + 1) && isCloserTok(k + 1)) k++;
		if (k + 1 >= tokEnd) {
			i = k + 1;
			continue;
		}
		const gap = (st[k + 1] as number) - ((st[k] as number) + (ln[k] as number));

		if (gap === 0) {
			// R10: no whitespace after the candidate. Never a split; flag "end.Next" run-togethers.
			const t = k + 1;
			if (
				cls[t] === TOK_WORD &&
				caseOf(text, (st[t] as number) - offset) === 1 &&
				i > tokStart &&
				cls[i - 1] === TOK_WORD &&
				(ln[i - 1] as number) >= 2 &&
				adjacent(i - 1, i)
			) {
				let exempt = false;
				const key = i === j ? abbrevKey(i) : null;
				if (key !== null) exempt = true;
				if (!exempt) {
					let a = (st[i - 1] as number) - offset;
					while (a > 0 && !isSpaceCode(text.charCodeAt(a - 1))) a--;
					let e = (st[t] as number) - offset + (ln[t] as number);
					while (e < text.length && !isSpaceCode(text.charCodeAt(e))) e++;
					const word = text.slice(a, e);
					if (word.includes('://') || URL_TLD_HINT.test(word)) exempt = true;
				}
				if (!exempt) flags |= SENT_NOSPACE;
			}
			i = k + 1;
			continue;
		}

		let p = k + 1;
		while (
			p < tokEnd &&
			(isOpenerTok(p) || (isEmphasisMark(p) && p + 1 < tokEnd && adjacent(p, p + 1)))
		)
			p++;
		if (p >= tokEnd) {
			i = k + 1;
			continue;
		}
		const pc = cls[p] as number;
		const pch = chAt(p);
		let split = true;

		// R1
		if (pc === TOK_WORD) {
			if (caseOf(text, (st[p] as number) - offset) === -1) {
				// lowercase-writer mode: the terminal still ends the sentence after a clause of some length
				const plain =
					!ell &&
					j - i < 1 &&
					k === j &&
					i > tokStart &&
					(cls[i - 1] === TOK_WORD
						? (ln[i - 1] as number) >= 2
						: cls[i - 1] === TOK_NUM ||
							(cls[i - 1] === TOK_PUNCT &&
								(chAt(i - 1) === 41 || chAt(i - 1) === 93) &&
								i - 2 >= tokStart &&
								cls[i - 2] === TOK_WORD)) &&
					adjacent(i - 1, i) &&
					!(
						i - 2 >= tokStart &&
						cls[i - 2] === TOK_PUNCT &&
						chAt(i - 2) === 46 &&
						adjacent(i - 2, i - 1)
					);
				if (!(lowerMode && plain && wc >= LOWER_MODE_MIN_WORDS)) split = false;
			}
		} else if (pc === TOK_PUNCT && (pch === 44 || pch === 59 || pch === 58)) {
			const nlBetween = text
				.slice((st[k] as number) + (ln[k] as number) - offset, (st[p] as number) - offset)
				.includes('\n');
			if (!(nlBetween && pch !== 44)) split = false; // a line-initial ':' or ';' (wiki indent) does not continue the sentence
		}
		if (ell) {
			if (pc === TOK_NUM) {
				// "choose... 14!" continues; "Gear..... 20. You wanted" starts a list item
				const marker =
					p + 2 < tokEnd &&
					isDotTok(p + 1) &&
					adjacent(p, p + 1) &&
					cls[p + 2] === TOK_WORD &&
					caseOf(text, (st[p + 2] as number) - offset) === 1;
				if (!marker) split = false;
			}
			if (i > tokStart && isOpenerTok(i - 1) && adjacent(i - 1, i)) split = false; // a quotation that opens with an ellipsis
			if (k > j && i > tokStart && adjacent(i - 1, i)) {
				const op = chAt(i - 1);
				const cl = chAt(k);
				if ((op === 91 && cl === 93) || (op === 40 && cl === 41)) split = false; // "[...]" / "(...)" editorial omission
			}
		}

		const single = i === j && cls[i] === TOK_PUNCT && chAt(i) === 46;
		if (split && single && i > tokStart) {
			const prev = i - 1;
			const pcls = cls[prev] as number;
			if (pcls === TOK_NUM && adjacent(prev, i)) {
				// R1 list numbers, R4 times
				const numText = tokens.fold(ids[prev] as number);
				if (/^\d{1,2}$/.test(numText)) {
					const v = Number(numText);
					const atStart = prev === sStart || (prev - 1 === sStart && cls[sStart] === TOK_PUNCT);
					let afterDelim = false;
					if (prev - 1 >= sStart && cls[prev - 1] === TOK_PUNCT) {
						const d = chAt(prev - 1);
						afterDelim = d === 58 || d === 59 || d === 44 || d === 40;
					}
					if (atStart || afterDelim || (listNext !== 0 && v === listNext)) {
						split = false;
						listNext = v + 1;
					} else listNext = 0;
				} else if (numText.includes(':') && pc === TOK_NUM) {
					const nl = text.indexOf('\n', (st[k] as number) + (ln[k] as number) - offset);
					if (nl < 0 || nl >= (st[p] as number) - offset) split = false;
				}
			} else if (pcls === TOK_WORD && adjacent(prev, i)) {
				// R2 abbreviations, R3 initials
				const key = abbrevKey(i);
				const isCapInitial =
					(ln[prev] as number) === 1 && caseOf(text, (st[prev] as number) - offset) === 1;
				const nextCap = pc === TOK_WORD && caseOf(text, (st[p] as number) - offset) === 1;
				if (isCapInitial && (key === null || !key.includes('.'))) {
					// R3 initial, except pronoun "I", unit/label letters ("20 K.", "Exhibit G.", "mmol/L.", "BDD&O.") that end the sentence
					let ends = false;
					const nlAfter = text
						.slice((st[k] as number) + (ln[k] as number) - offset, (st[p] as number) - offset)
						.includes('\n');
					const nextIsInitial =
						nextCap &&
						(ln[p] as number) === 1 &&
						p + 1 < tokEnd &&
						isDotTok(p + 1) &&
						adjacent(p, p + 1);
					if (
						(nextCap || (nlAfter && cls[prev - 1] === TOK_NUM)) &&
						!nextIsInitial &&
						prev - 1 >= tokStart
					) {
						let q = prev - 1;
						while (q > tokStart && isOpenerTok(q)) q--;
						const letter = rawOf(prev);
						if (cls[q] === TOK_WORD) {
							const w = tokens.fold(ids[q] as number);
							const lowerStart = caseOf(text, (st[q] as number) - offset) === -1;
							if (letter === 'I' && lowerStart && !NAME_PARTICLES.has(w)) ends = true;
							if (LABEL_WORDS.has(w)) ends = true;
						} else if (cls[q] === TOK_NUM && !adjacent(q, prev)) ends = true;
						else if (
							cls[q] === TOK_PUNCT &&
							adjacent(q, prev) &&
							(chAt(q) === 38 || chAt(q) === 47)
						)
							ends = true;
					}
					if (!ends) split = false;
				} else if (key !== null) {
					if (isStrong(key)) {
						const special = key === 'etc' || key === 'a.m' || key === 'p.m';
						const rawPrev = rawOf(prev);
						if (special && pc === TOK_WORD && caseOf(text, (st[p] as number) - offset) === 1) {
							split = !CONTINUES_TIME.has(tokens.fold(ids[p] as number));
						} else if (
							nextCap &&
							AMBIG_ACRONYM.has(key) &&
							rawPrev.length >= 2 &&
							rawPrev === rawPrev.toUpperCase() &&
							rawOf(p) !== rawOf(p).toUpperCase()
						) {
							split = true; // "MS. Next", "9 a.m. EST. Thirteen days": an all-caps acronym, not the abbreviation
						} else if (
							key === 'al' &&
							nextCap &&
							SENTENCE_OPENERS.has(tokens.fold(ids[p] as number))
						) {
							split = true; // "Haaf et al. This study"
						} else if (
							UNITS.has(key) &&
							nextCap &&
							prev - 1 >= tokStart &&
							cls[prev - 1] === TOK_NUM
						) {
							split = true; // "0.5 cm. Seated"
						} else split = false;
					} else if (pc === TOK_NUM) {
						split = false;
					} else if (pc === TOK_PUNCT && (pch === 41 || pch === 93 || pch === 125)) {
						split = false; // "(see Fig. )" with the number removed
					} else if (
						pc === TOK_WORD &&
						p + 1 < tokEnd &&
						cls[p + 1] === TOK_PUNCT &&
						chAt(p + 1) === 46 &&
						adjacent(p, p + 1) &&
						abbreviated(tokens.fold(ids[p] as number))
					) {
						split = false; // "Mon. Dec. 5"
					}
				} else if (
					(ln[prev] as number) === 1 &&
					caseOf(text, (st[prev] as number) - offset) === 1
				) {
					split = false;
				}
			}
		}
		// inline list "... steps 1. Cook it 2. Mix it": a "1." with a later " 2." in the block is a list marker
		if (
			split &&
			single &&
			i > tokStart &&
			cls[i - 1] === TOK_NUM &&
			adjacent(i - 1, i) &&
			tokens.fold(ids[i - 1] as number) === '1' &&
			listNext === 0
		) {
			const from = (st[k] as number) + (ln[k] as number) - offset;
			if (/\s2\.\s/.test(text.slice(from, from + 700))) {
				split = false;
				listNext = 2;
			}
		}

		// R6b: dialogue tag after `!` / `?` and a closing quote.
		if (split && k > j && pc === TOK_WORD && caseOf(text, (st[p] as number) - offset) === 1) {
			let bangQ = ell;
			for (let t = i; t <= j; t++) {
				const ch = chAt(t);
				if (ch === 33 || ch === 63) bangQ = true;
			}
			let quoteCloser = false;
			for (let t = j + 1; t <= k; t++) {
				const ch = chAt(t);
				if (ch === 34 || ch === 0x201d || ch === 0x2019 || ch === 39) quoteCloser = true;
			}
			if (bangQ && quoteCloser && isOpenerTok(sStart) && p + 1 < tokEnd) {
				const q = p + 1;
				if (cls[q] === TOK_WORD) {
					if (SPEECH_VERBS.has(tokens.fold(ids[q] as number))) split = false;
					else if (
						caseOf(text, (st[q] as number) - offset) === 1 &&
						q + 1 < tokEnd &&
						cls[q + 1] === TOK_WORD &&
						SPEECH_VERBS.has(tokens.fold(ids[q + 1] as number))
					)
						split = false;
				}
			}
		}

		if (split && content) {
			if (wc > RUNON_WORDS) flags |= SENT_RUNON;
			emit(sStart, k + 1, flags, true);
			sStart = k + 1;
			content = false;
			wc = 0;
			flags = 0;
		} else if (split && !content && hasPrev && sStart < k + 1) {
			// a punctuation-only tail (")." / "[…].") belongs to the sentence before it
			emit(sStart, k + 1, flags, false);
			sStart = k + 1;
			wc = 0;
			flags = 0;
		}
		i = k + 1;
	}

	if (wc > RUNON_WORDS) flags |= SENT_RUNON;
	emit(sStart, tokEnd, flags, content);
	if (hasPrev) out.push(pS, pE, pF);
}
